import { useState, FormEvent } from "react";
import { motion } from "framer-motion";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { fadeIn, staggerContainer } from "@/lib/animations";

interface FormData {
  name: string;
  email: string;
  company: string;
  message: string;
}

export function ContactSection() {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    company: "",
    message: ""
  });
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};
    
    if (!formData.name || formData.name.length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }
    
    if (!formData.email || !/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    
    if (!formData.company) {
      newErrors.company = "Company name is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      // Use apiRequest which handles API base URL
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const apiUrl = `${apiBaseUrl}/api/waitlist`;
      
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error("Failed to submit form");
      }
      
      setIsSuccess(true);
      setFormData({
        name: "",
        email: "",
        company: "",
        message: ""
      });
      
      toast({
        title: "Success!",
        description: "Thank you for joining our waitlist. We'll be in touch soon.",
        variant: "default",
      });
    } catch (error) {
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gray-950">
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.25 }}
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        <motion.div 
          variants={fadeIn("up", "tween", 0.2, 1)}
          className="max-w-3xl mx-auto text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-light text-white">Ready to transform your hiring with Vettor?</h2>
          <p className="mt-4 text-xl text-gray-400 font-light">Join our early access program and be among the first to experience our premium AI agent workflow for recruiting</p>
        </motion.div>
        
        <motion.div
          variants={fadeIn("up", "tween", 0.3, 1)}
          className="max-w-xl mx-auto"
        >
          <div className="bg-gray-800/70 backdrop-blur-md border border-gray-700 rounded-xl shadow-lg overflow-hidden">
            <div className="p-8">
              {isSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-violet-900/30 text-violet-400 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-light text-white mb-2">Thank you for joining our waitlist!</h3>
                  <p className="text-gray-400 font-light">We'll be in touch soon with more information about early access.</p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-light text-gray-200">
                      Full Name
                    </label>
                    <Input 
                      id="name"
                      name="name"
                      placeholder="Jane Smith" 
                      value={formData.name}
                      onChange={handleChange}
                      className={errors.name ? "border-red-500 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500" : "bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"}
                    />
                    {errors.name && (
                      <p className="text-sm text-red-400">{errors.name}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-light text-gray-200">
                      Work Email
                    </label>
                    <Input 
                      id="email"
                      name="email"
                      placeholder="jane@company.com" 
                      value={formData.email}
                      onChange={handleChange}
                      className={errors.email ? "border-red-500 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500" : "bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"}
                    />
                    {errors.email && (
                      <p className="text-sm text-red-400">{errors.email}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="company" className="text-sm font-light text-gray-200">
                      Company
                    </label>
                    <Input 
                      id="company"
                      name="company"
                      placeholder="Acme Inc." 
                      value={formData.company}
                      onChange={handleChange}
                      className={errors.company ? "border-red-500 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500" : "bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"}
                    />
                    {errors.company && (
                      <p className="text-sm text-red-400">{errors.company}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="message" className="text-sm font-light text-gray-200">
                      How can we help?
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Tell us about your recruiting needs..."
                      className="resize-none bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                      value={formData.message}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full bg-violet-600 hover:bg-violet-700 text-white border-0 font-light"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Joining..." : "Join the Waitlist"}
                  </Button>
                </form>
              )}
            </div>
          </div>
          <p className="text-center text-gray-500 text-sm mt-6 font-light">
            By submitting this form, you agree to our <a href="#" className="text-violet-400 hover:text-violet-300">Privacy Policy</a> and <a href="#" className="text-violet-400 hover:text-violet-300">Terms of Service</a>.
          </p>
        </motion.div>
      </motion.div>
    </section>
  );
}
